import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type {
  ArrayConfigFieldData,
  ConfigService,
  ObjectConfigFieldData,
} from '@empathyco/playboard-core-domain'
import type { ComponentOptions } from 'vue'
import type Vue from 'vue'
import type { NavigationGuard, Route } from 'vue-router'
import type { Component } from 'vue-router/types/router'

import type {
  AppRouteConfig,
  DynamicFilterConfig,
  GuardResolveFunction,
  RouteFiltersConfig,
} from '../../models'
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { mapGetters } from '../../composables/utils'

import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum, FilterIdEnum } from '../../enums'
import { CONFIG_CONSTS } from '../../store/config/config.consts'

import SearchConfigurationsForm from '../../views/configurations/search-configurations-form.view.vue'
import SearchConfigurationsList from '../../views/configurations/search-configurations-list.view.vue'
import SearchQueryRankingForm from '../../views/search-query-ranking/search-query-ranking-form.view.vue'
import SearchQueryRankingList from '../../views/search-query-ranking/search-query-ranking-list.view.vue'

import { ROUTER_CONSTS } from '../router.consts'
import { ENABLED_FILTER_CONFIG, ENDPOINT_FILTER_CONFIG } from '../routes.filters'
import { ROUTES_PATHS } from '../routes.paths'

const NAMES = ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION]

const PATHS = Object.assign(ROUTES_PATHS, {
  root: 'configurations',
})

interface Mixin extends Vue {
  beforeRouteLeave?: NavigationGuard
}

const featureId = FeatureIdEnum.CONFIGURATION

const useSearchQueryRanking = (instance: Instance): boolean => {
  return instance.domains[DomainEnum.SEARCH]?.tools[featureId]?.useSearchConfigurations ?? true
}

const getQueryRankingComponent = (
  searchQueryRankingComponent: Component,
  searchConfigurationsComponent: Component,
): Component =>
  defineComponent({
    beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
      const component = this.component as ComponentOptions<Vue>
      const mixin = (component.mixins as Array<Mixin>)?.find(
        mixin => typeof mixin.beforeRouteLeave === 'function',
      )

      if (component.beforeRouteLeave) {
        component.beforeRouteLeave.call(this.root, to, from, next)
      } else if (mixin?.beforeRouteLeave) {
        mixin.beforeRouteLeave.call(this.root, to, from, next)
      } else {
        next()
      }
    },
    setup() {
      const ctx = getCurrentInstance()
      if (!ctx) {
        throw new Error("'ctx' is not defined")
      }
      const {
        proxy: { $store },
      } = ctx

      const root = ref()

      const { currentInstance } = mapGetters<Instance>($store, CONFIG_CONSTS.moduleName, {
        currentInstance: CONFIG_CONSTS.getters.currentInstance,
      })

      const component = computed<Component>(() => {
        return useSearchQueryRanking(currentInstance.value)
          ? searchQueryRankingComponent
          : searchConfigurationsComponent
      })

      return {
        root,
        component,
      }
    },
    template: '<component ref="root" :is="component" v-on="$listeners" v-bind="$props" />',
  }) as Component

const List = getQueryRankingComponent(SearchQueryRankingList, SearchConfigurationsList)
const Form = getQueryRankingComponent(SearchQueryRankingForm, SearchConfigurationsForm)

const listFilters: AppRouteConfig['meta']['filters'] = async instance => {
  const instanceManagementService = CONTAINER.get<ConfigService>(TYPES.ConfigService)
  const serviceConfigs = await instanceManagementService.read({
    instance: instance.id,
    service: 'playboard',
  })

  const endpointsArray = (
    (serviceConfigs.data as ObjectConfigFieldData).endpoints as ObjectConfigFieldData
  ).endpoints as ArrayConfigFieldData

  let filters: RouteFiltersConfig = {
    extra: {
      ...(endpointsArray.length > 1 && { [ENDPOINT_FILTER_CONFIG.id]: ENDPOINT_FILTER_CONFIG }),
      [ENABLED_FILTER_CONFIG.id]: ENABLED_FILTER_CONFIG,
    },
  }
  if (useSearchQueryRanking(instance)) {
    filters = {
      ...filters,
      regularFiltersEnabled: true,
      config: {
        [FilterIdEnum.DEVICE]: {
          enabled: false,
        },
      },
    }
  } else {
    const toolConfig = instance.domains[DomainEnum.SEARCH]?.tools[ActionTypeEnum.CONFIGURATION]
    const config = toolConfig?.pivotIds?.reduce(
      (result: Record<string, DynamicFilterConfig>, pivotId) => ({
        ...result,
        [pivotId]: {
          enabled: true,
        },
      }),
      {},
    )
    filters = {
      ...filters,
      config,
    }
  }
  return filters
}

export const SEARCH_QUERY_RANKING_ROUTES: AppRouteConfig[] = [
  {
    name: NAMES.search.list,
    path: PATHS.root,
    component: List,

    meta: {
      featureId,
      browseListFilterEnabled: true,
      filters: listFilters,
      style: {
        removeContentPadding: true,
      },
    },
  },
  {
    name: NAMES.search.create,
    path: `${PATHS.root}/${PATHS.actions.create}`,
    component: Form,
    meta: {
      featureId,
      titleI18nKey: 'createConfiguration',
      parentRouteName: NAMES.search.list,
    },
  },
  {
    name: NAMES.search.update,
    path: `${PATHS.root}/${PATHS.actions.update}`,
    component: Form,
    meta: {
      featureId,
      titleI18nKey: 'updateConfiguration',
      parentRouteName: NAMES.search.list,
    },
  },
]
